import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getCsrfCookie } from "../lib/utils";
import { REEPRISE_LIST_QUERY_KEY } from "./useGeneratedTextPaginatedList";
import { TEXT_STATISTICS_QUERY_KEY } from "./useTextStatistics";
import { invalidateQuery as invalidateStatistics } from "./useVocabularyStatistics";
import { GeneratedText } from "./types/GeneratedTexts";
import { REEPRISE_DETAILS_QUERY_KEY } from "./useGeneratedTextDetails";
import { writeGeneratedTextPromptCacheEntry } from "../utils/generatedTextPromptCache";

interface CreateReepriseDto {
  text_type: string;
  language: string;
  level: string;
  tense: string;
  word_number: number;
  topic: string;
  main_character: string;
  topic_source?: "MANUAL" | "IDEA";
  topic_suggestion_id?: number;
  image?: FileList;
}

export function useGeneratedTextCreate() {
  const queryClient = useQueryClient();

  const createGeneratedTextMutation = useMutation({
    mutationFn: async (arg: { createReepriseDto: CreateReepriseDto }) => {
      const formData = new FormData();
      formData.append("text_type", arg.createReepriseDto.text_type);
      formData.append("language", arg.createReepriseDto.language);
      formData.append("level", arg.createReepriseDto.level);
      formData.append("tense", arg.createReepriseDto.tense);
      formData.append("word_number", "" + arg.createReepriseDto.word_number);
      formData.append("topic", arg.createReepriseDto.topic);
      formData.append("main_character", arg.createReepriseDto.main_character);
      if (arg.createReepriseDto.topic_source) {
        formData.append("topic_source", arg.createReepriseDto.topic_source);
      }
      if (arg.createReepriseDto.topic_suggestion_id) {
        formData.append(
          "topic_suggestion_id",
          `${arg.createReepriseDto.topic_suggestion_id}`,
        );
      }
      formData.append("publicly_visible", "false");
      if (arg.createReepriseDto.image && arg.createReepriseDto.image.length > 0)
        formData.append("image", arg.createReepriseDto.image[0]);

      const res = await fetch("/api/reeprise/generated_texts/", {
        method: "POST",
        headers: {
          "X-CSRFTOKEN": getCsrfCookie(),
        },
        body: formData,
      });
      if (!res.ok) {
        throw new Error("Could not generate text");
      }
      const created = (await res.json()) as GeneratedText;
      writeGeneratedTextPromptCacheEntry({
        textId: created.id,
        topic: arg.createReepriseDto.topic,
      });
      return created;
    },
    onSettled: (data) => {
      if (data !== undefined) {
        void queryClient.setQueryData(
          [REEPRISE_DETAILS_QUERY_KEY, `${data.id}`],
          data,
        );
      }
      void queryClient.invalidateQueries({
        queryKey: [REEPRISE_LIST_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [TEXT_STATISTICS_QUERY_KEY],
      });
      invalidateStatistics(queryClient);
    },
  });

  return createGeneratedTextMutation;
}
